// Orbit card stack for the "made for the moments between walks" section (like componentry.dev
// "orbit card stack"). At rest the cards sit in one neat pile; hovering, focusing or tapping fans the
// deck out into an arc and lifts the card under the pointer. Phones: a swipeable row (CSS).
// Set img on a card to an asset file name to replace its photo placeholder.
(function () {
  const { useEffect, useRef, useState } = React;
  const h = React.createElement;
  const BASE = window.ASSET_BASE || "";

  const CARDS = [
    { num: "01", title: "Built to last", text: "Materials that survive daily wear, saltwater, mud, and the occasional chew session.", tag: "Durability", accent: "#f04923", img: "story-card-1.webp", alt: "A rust harness and matching lead resting on a stone ledge beside a dog's paw" },
    { num: "02", title: "Home-friendly", text: "Muted tones that blend into your space \u2014 no neon nylon, no cartoon prints.", tag: "Design", accent: "#9bb08a", img: "story-card-2.webp", alt: "A silver tabby curled up in a sage-green bed in a calm living room" },
    { num: "03", title: "Vet-approved", text: "Fit-tested with veterinarians and trainers for safety and comfort.", tag: "Safety", accent: "#e8c07a", img: "story-card-3.webp", alt: "A vet fitting an orange harness on a smiling beagle" },
    { num: "04", title: "Plastic-lite", text: "Recycled packaging and solid brass hardware \u2014 because our pets share this planet too.", tag: "Planet", accent: "#7fb7c9", img: "story-card-4.webp", alt: "A rust lead packed in a recycled kraft-paper box" },
    { num: "05", title: "Pet-parent picked", text: "Chosen and tried by our own dogs and cats before it ever reaches the shop.", tag: "Care", accent: "#d99ab0", img: "story-card-5.webp", alt: "A pet parent fitting a harness on a happy golden retriever" },
  ];
  const REST = 2;                 // card on top while the deck is closed
  const PAW_D = "M12 21c-2.2 0-4.6-1.3-4.6-3.6 0-2.4 2.3-4.9 4.6-4.9s4.6 2.5 4.6 4.9c0 2.3-2.4 3.6-4.6 3.6ZM5.2 12.6c-1.2.3-2.5-.7-2.9-2.3-.4-1.6.2-3.1 1.4-3.4 1.2-.3 2.5.7 2.9 2.3.4 1.6-.2 3.1-1.4 3.4Zm13.6 0c1.2.3 2.5-.7 2.9-2.3.4-1.6-.2-3.1-1.4-3.4-1.2-.3-2.5.7-2.9 2.3-.4 1.6.2 3.1 1.4 3.4ZM9.1 8.7c-1.3.1-2.4-1.3-2.5-3.1-.1-1.8.9-3.4 2.2-3.5 1.3-.1 2.4 1.3 2.5 3.1.1 1.8-.9 3.4-2.2 3.5Zm5.8 0c1.3.1 2.4-1.3 2.5-3.1.1-1.8-.9-3.4-2.2-3.5-1.3-.1-2.4 1.3-2.5 3.1-.1 1.8.9 3.4 2.2 3.5Z";

  function PtOrbitStack() {
    const stage = useRef(null);
    const [active, setActive] = useState(REST);
    const [open, setOpen] = useState(false);
    const [spread, setSpread] = useState(90);
    const mid = (CARDS.length - 1) / 2;

    // Fan width follows the space available, so the open deck never spills out of its column.
    useEffect(() => {
      const el = stage.current; if (!el) return;
      const fit = () => {
        const card = el.querySelector(".os-card");
        const free = el.clientWidth - (card ? card.offsetWidth : 280);
        setSpread(Math.max(18, Math.min(215, free / (CARDS.length - 1))));
      };
      fit();
      const ro = window.ResizeObserver ? new ResizeObserver(fit) : null;
      if (ro) ro.observe(el); else window.addEventListener("resize", fit);
      return () => { if (ro) ro.disconnect(); else window.removeEventListener("resize", fit); };
    }, []);

    const activate = (i) => { setOpen(true); setActive(i); };
    const close = () => { setOpen(false); setActive(REST); };
    const onKey = (e, i) => {
      const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const n = (i + step + CARDS.length) % CARDS.length;
      activate(n);
      const next = stage.current && stage.current.querySelectorAll(".os-card")[n];
      if (next) next.focus();
    };

    return h("div", {
      ref: stage, className: "os-stage" + (open ? " is-open" : ""), role: "list", "aria-label": "Why pet parents choose us",
      onMouseLeave: close,
      onBlur: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) close(); },
    },
      CARDS.map((c, i) => {
        const orbit = i - mid, stack = i - REST, on = i === active;
        const x = open ? orbit * spread : stack * 10;
        const y = open ? Math.abs(orbit) * 30 + Math.max(0, Math.abs(orbit) - 1) * 10 - (on ? 34 : 0) : Math.abs(stack) * 5;
        const r = open ? orbit * 8.5 : stack * 2.8;
        return h("article", {
          key: c.num, role: "listitem", tabIndex: 0, className: "os-card" + (on ? " is-active" : ""),
          "aria-current": on ? "true" : undefined,
          style: { zIndex: on ? 80 : 50 - Math.abs(i - active), transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) rotate(${r}deg) scale(${open ? 0.985 : 0.97})`, "--os-accent": c.accent },
          onMouseEnter: () => activate(i), onFocus: () => activate(i), onClick: () => activate(i), onKeyDown: (e) => onKey(e, i),
        },
          h("div", { className: "os-photo" },
            c.img ? h("img", { src: BASE + c.img, alt: c.alt || c.title, width: 720, height: 540, loading: "lazy", decoding: "async" })
                  : h("svg", { viewBox: "0 0 24 24", focusable: "false", "aria-hidden": "true" }, h("path", { d: PAW_D })),
            h("span", { className: "os-num" }, c.num)),
          h("div", { className: "os-body" },
            h("div", { className: "os-row" },
              h("h4", { className: "os-title" }, c.title),
              h("span", { className: "os-tag" }, c.tag)),
            h("p", { className: "os-text" }, c.text)));
      }));
  }

  window.PtOrbitStack = PtOrbitStack;
})();
